import React, { useState, useEffect } from 'react';
import { InputText } from 'primereact/inputtext';
import { Password } from 'primereact/password';
import { Button } from 'primereact/button';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import i18n from 'i18next';
import classNames from 'classnames';

const RegisterPage = ({ }) => {
    const params = new URLSearchParams(window.location.search);
    const inviteCode = params.get('inviteCode') || '';
    const [user, setUser] = useState({});
    const [nicknameValid, setNicknameValid] = useState(true);
    const [emailValid, setEmailValid] = useState(true);
    const [validationErrors, setValidationErrors] = useState({});
    const [errorMessage, setErrorMessage] = useState();
    const [isLoading, setIsLoading] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false)

    useEffect(() => {
        axios.get('/Register/SignupAccount', {
            params: { inviteCode: inviteCode }
        })
            .then(response => {
                const data =  response.data
                setUser(data)
            })
            .catch(error => {
                console.error(error);
            });
    }, []);

    const handleInputChange = (e, field) => {
        const value = e.target.value;
        const updatedUser = { ...user, [field]: value };
        setUser(updatedUser);
        // Clear error when user starts typing
        if (validationErrors[field]) {
            setValidationErrors(prev => ({ ...prev, [field]: null }));
        }
    };

    const checkNickname = () => {
        if (!user.NickName) return;
        axios.post('/Register/CheckNickName', { NickName: user.NickName })
            .then((res) => {
                setNicknameValid(res.data.IsSuccess)
                if(!res.data.IsSuccess) {
                    setValidationErrors(prev => ({ ...prev, ["NickName"]: i18n.t('register.nickname_exists') }));
                }
            })
            .catch(() => setNicknameValid(false));
    };

    const checkEmail = () => {
        if (!user.UserEmail || !user.UserEmail.includes('@')) return;
        axios.post('/Register/CheckEmail', { UserEmail: user.UserEmail })
            .then((res) => {
                setEmailValid(res.data.IsSuccess);
                if(!res.data.IsSuccess) {
                    setValidationErrors(prev => ({ ...prev, ["UserEmail"]: i18n.t('register.email_exists') }));
                }
            })
            .catch(() => setEmailValid(false));
    };

    const validateUser = () => {
        const errors = {};
        
        // First Name validation
        if (!user.FirstName?.trim()) {
            errors.FirstName = i18n.t('register.first_name_required');
        }

        // Last Name validation
        if (!user.LastName?.trim()) {
            errors.LastName = i18n.t('register.last_name_required');
        }

        // Nickname validation
        if (!user.NickName?.trim()) {
            errors.NickName = i18n.t('register.nickname_required');
        } else if (!nicknameValid) {
            errors.NickName = i18n.t('register.nickname_exists');
        }

        // Email validation
        if (!user.UserEmail?.trim()) {
            errors.UserEmail = i18n.t('register.email_required');
        } else if (!user.UserEmail.includes('@')) {
            errors.UserEmail = i18n.t('register.invalid_email');
        } else if (!emailValid) {
            errors.UserEmail = i18n.t('register.email_exists');
        }

        // Password validation
        if (!user.Password) {
            errors.Password = i18n.t('register.password_required');
        } else if (user.Password.length < 8) {
            errors.Password = i18n.t('register.password_length');
        }

        // Confirm Password validation
        if (!user.ConfirmedPassword) {
            errors.ConfirmedPassword = i18n.t('register.confirm_password_required');
        } else if (user.Password !== user.ConfirmedPassword) {
            errors.ConfirmedPassword = i18n.t('register.passwords_mismatch');
        }

        setValidationErrors(errors);
        return {
            isValid: Object.keys(errors).length === 0,
            errors
        };
    };

    const handleSubmit = async (e) => {
        if (e) {
            e.preventDefault();
        }
        
        const validation = validateUser();
        if (!validation.isValid) {
            return;
        }

        setIsLoading(true);
        try {
            const response = await axios.post('/Register/Register', user);
            const data = response.data;
            if(data.IsSuccess){
                setIsSuccess(true);
            }else{
                setErrorMessage(data.ErrorMessage)
            }
        } catch (error) {
            setUser((prev) => ({ ...prev, ErrorMessage: i18n.t('register.submission_failed') }));
        } finally {
            setIsLoading(false);
        }
    };
    
    return (
        <div className="register-inner">
            <div className="registerbody-inner">
                {!isSuccess && (
                    <form id="registerForm" onSubmit={handleSubmit} autoComplete="off">
                        <div>
                            <h3 className="card-title mb-4">{i18n.t('register.create_account')}</h3>

                            <div className="grid">
                                <div className="col-6">
                                    <InputText name="NickName" className={classNames('w-full', { 'p-invalid': !!validationErrors.NickName })} placeholder={i18n.t('register.nick_name')} value={user.NickName || ''} onBlur={checkNickname} onChange={(e) => handleInputChange(e, 'NickName')} autoComplete='off' autoFocus />
                                    {validationErrors.NickName && <small className="p-error" id="errorNickNameMsgId">{validationErrors.NickName}</small>}
                                </div>
                            </div>
                            <div className="grid mt-3">
                                <div className="col-6">
                                    <InputText name="FirstName" className={classNames('w-full', { 'p-invalid': !!validationErrors.FirstName })} placeholder={i18n.t('register.first_name')} value={user.FirstName || ''} onChange={(e) => handleInputChange(e, 'FirstName')} autoComplete='off'  />
                                    {validationErrors.FirstName && <small className="p-error">{validationErrors.FirstName}</small>}
                                </div>

                                <div className="col-6">
                                    <InputText name="LastName" className={classNames('w-full', { 'p-invalid': !!validationErrors.LastName })} placeholder={i18n.t('register.last_name')} value={user.LastName || ''} onChange={(e) => handleInputChange(e, 'LastName')} autoComplete='off' />
                                    {validationErrors.LastName && <small className="p-error">{validationErrors.LastName}</small>}
                                </div>
                            </div>

                            <div className="grid mt-3">
                                <div className="col-12">
                                    <InputText name="UserEmail" type="email" className={classNames('w-full', { 'p-invalid': !!validationErrors.UserEmail })} placeholder={i18n.t('register.email')} value={user.UserEmail || ''} onBlur={checkEmail} onChange={(e) => handleInputChange(e, 'UserEmail')} id="userEmailBox" autoComplete='off' />
                                    {validationErrors.UserEmail && <small className="p-error" id="errorEmailMsgId">{validationErrors.UserEmail}</small>}
                                </div> {/* disabled={user.IsInvitation} */}
                            </div>

                            <div className="grid mt-3">
                                <div className="col-6">
                                    <Password name="Password" inputClassName='w-full' className={classNames('w-full', { 'p-invalid': !!validationErrors.Password })} placeholder={i18n.t('register.password')}
                                        feedback={false} onChange={(e) => handleInputChange(e, 'Password')} autoComplete='off' toggleMask />
                                    {validationErrors.Password && <small className="p-error" id="pwdValid">{validationErrors.Password}</small>}
                                </div>

                                <div className="col-6">
                                    <Password name="ConfirmedPassword" inputClassName='w-full' className={classNames('w-full', { 'p-invalid': !!validationErrors.ConfirmedPassword })} placeholder={i18n.t('register.confirm_password')}
                                        feedback={false} onChange={(e) => handleInputChange(e, 'ConfirmedPassword')} autoComplete='off' toggleMask />
                                    {validationErrors.ConfirmedPassword && <small className="p-error" id="cfpwdValid">{validationErrors.ConfirmedPassword}</small>}
                                </div>
                            </div>

                            <div id="validator" className="mt-3">
                                {errorMessage && <div className="p-error" id="errorMsgId">{errorMessage}</div>}
                            </div>

                            <div className="grid mt-3">
                                {!user.IsInvitation && (
                                    <div className="col-6">
                                        <a href='/login' className="text-center text-primary">
                                            {i18n.t('register.sign_in_instead')}
                                        </a>
                                    </div>
                                )}
                                <div className={user.IsInvitation ? "col-12" : "col-6"}>
                                    <Button type="submit" label={i18n.t('register.sign_up')} className="w-full" id="UserRegister" loading={isLoading} disabled={isLoading} />
                                </div>
                            </div>
                        </div>
                    </form>
                )}
                {isSuccess  && (
                    <div className='login-form'>
                        <div className="text-2xl font-bold text-center">
                            {i18n.t('register.register_successfully')}
                        </div>

                        <div className="text-center" style={{marginTop: '10px' }}>
                            <a href='/login' className="text-center text-primary">{i18n.t('register.go_back_to_login')}</a>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default RegisterPage;
